import { useEffect, useRef, useState } from "react";
import { Download, Headphones, Loader2, RotateCcw, RotateCw, ShieldCheck, Video } from "lucide-react";
import { documentError, documentPlayback, downloadDocument, formatBytes } from "./documentService";
import { buttonClass, DocumentDialog, DocumentError, fieldClass } from "./DocumentUI";

function RecordingMedia({ document, session, position, onRenew }) {
  const media = useRef(null);
  const renewing = useRef(false);
  const [buffering, setBuffering] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [speed, setSpeed] = useState(position.current.speed);
  const expired = () => Date.now() >= session.expiresAt - 5000;
  const renew = () => {
    if (renewing.current) return;
    renewing.current = true;
    onRenew();
  };
  useEffect(() => {
    const element = media.current;
    renewing.current = false;
    if (element.getAttribute("src") !== session.url) element.setAttribute("src", session.url);
    return () => {
      // Closing the dialog, changing leads/workspaces, or renewing access must
      // stop playback and release the media connection, not leave hidden audio.
      renewing.current = true;
      element.pause();
      element.removeAttribute("src");
      element.load();
    };
  }, [session.url]);
  const loaded = () => {
    const element = media.current;
    element.playbackRate = position.current.speed;
    element.volume = position.current.volume;
    element.muted = position.current.muted;
    if (position.current.time > 0 && Number.isFinite(element.duration)) {
      element.currentTime = Math.min(position.current.time, Math.max(0, element.duration - 0.1));
    }
    setReady(true); setBuffering(false);
    if (position.current.playing) element.play().catch(() => { position.current.playing = false; });
  };
  const failed = () => {
    if (renewing.current) return;
    if (expired()) { renew(); return; }
    position.current.playing = false;
    setBuffering(false); setReady(false);
    setError(media.current?.error?.code === 4 || media.current?.error?.code === 3
      ? "Your browser cannot play this recording’s format or codec. Download the original to open it in a compatible player."
      : "The recording could not load. Check your connection and retry. You can also download the original file.");
  };
  const seek = (seconds) => {
    const element = media.current;
    const next = Math.min(Math.max(0, element.currentTime + seconds), Number.isFinite(element.duration) ? element.duration : Infinity);
    position.current.time = next;
    if (expired()) renew(); else element.currentTime = next;
  };
  const Media = session.kind === "video" ? "video" : "audio";
  return <div>
    <div className={`relative overflow-hidden rounded-xl border ${session.kind === "video" ? "border-slate-800 bg-slate-950" : "border-[#dcebf3] bg-[#f2f9fc] p-4 sm:p-6"}`}>
      {session.kind === "audio" && <div className="mb-5 flex items-center gap-3"><span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-[#1689c1] shadow-sm"><Headphones className="h-6 w-6" /></span><div><p className="text-sm font-semibold text-[#17384e]">Audio recording</p><p className="mt-1 text-sm text-[#607d8e]">Listen and review at your own pace.</p></div></div>}
      <Media ref={media} src={session.url} controls playsInline preload="metadata" aria-label={`${session.kind === "video" ? "Video" : "Audio"} recording: ${document.title}`}
        className={session.kind === "video" ? "aspect-video max-h-[55dvh] w-full" : "h-12 w-full"}
        onLoadedMetadata={loaded} onCanPlay={() => setBuffering(false)} onWaiting={() => setBuffering(true)} onPlaying={() => setBuffering(false)}
        onPlay={() => { position.current.playing = true; if (expired()) renew(); }}
        onPause={(event) => { if (!renewing.current && !event.currentTarget.error) position.current.playing = false; }}
        onEnded={() => { position.current.playing = false; }}
        onTimeUpdate={(event) => { if (!renewing.current && !event.currentTarget.error) position.current.time = event.currentTarget.currentTime; }}
        onSeeking={(event) => { if (!renewing.current) { position.current.time = event.currentTarget.currentTime; if (expired()) renew(); } }}
        onRateChange={(event) => { position.current.speed = event.currentTarget.playbackRate; setSpeed(event.currentTarget.playbackRate); }}
        onVolumeChange={(event) => { position.current.volume = event.currentTarget.volume; position.current.muted = event.currentTarget.muted; }} onError={failed}>
        Your browser does not support in-app playback. Download the recording instead.
      </Media>
      {buffering && !error && <p role="status" className={`pointer-events-none flex items-center justify-center gap-2 text-sm ${session.kind === "video" ? "absolute inset-x-0 top-3 text-white" : "mt-3 text-[#607d8e]"}`}><Loader2 className="h-4 w-4 animate-spin" />Loading recording…</p>}
    </div>
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
      <div className="flex gap-2"><button type="button" disabled={!ready} onClick={() => seek(-10)} className={buttonClass} aria-label="Back 10 seconds"><RotateCcw className="h-4 w-4" />10s</button><button type="button" disabled={!ready} onClick={() => seek(10)} className={buttonClass} aria-label="Forward 10 seconds">10s<RotateCw className="h-4 w-4" /></button></div>
      <label className="flex items-center gap-2 text-sm text-[#607d8e]">Speed<select aria-label="Playback speed" value={speed} disabled={!ready} onChange={(event) => { media.current.playbackRate = Number(event.target.value); position.current.speed = Number(event.target.value); setSpeed(Number(event.target.value)); }} className={`${fieldClass} w-24`}>{[0.75, 1, 1.25, 1.5, 2].map((value) => <option key={value} value={value}>{value}×</option>)}</select></label>
    </div>
    {error && <div className="mt-4 space-y-3"><DocumentError message={error} /><button type="button" onClick={renew} className={buttonClass}>Retry playback</button></div>}
  </div>;
}

export default function DocumentPlayerDialog({ workspaceId, document, onClose }) {
  return <DocumentDialog title="Recording player" wide onClose={onClose}><RecordingPlayerContent workspaceId={workspaceId} document={document} /></DocumentDialog>;
}

export function RecordingPlayerContent({ workspaceId, document }) {
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const position = useRef({ time: 0, playing: false, speed: 1, volume: 1, muted: false });
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setSession(null); setError("");
    // Never prefetch recordings from list rows, persist URLs, or download full
    // blobs into JS memory. Only the opened native player fetches media bytes.
    const startedAt = Date.now();
    documentPlayback(workspaceId, document.id, controller.signal).then((result) => {
      if (!controller.signal.aborted) setSession({ ...result, expiresAt: startedAt + result.expires_in * 1000 });
    }).catch((error) => { if (!controller.signal.aborted) setError(documentError(error)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [workspaceId, document.id, refresh]);
  const download = async () => {
    setDownloading(true); setDownloadError("");
    try { await downloadDocument(workspaceId, document.id); }
    catch (error) { if (alive.current) setDownloadError(documentError(error)); }
    finally { if (alive.current) setDownloading(false); }
  };
  const Icon = document.kind === "audio" ? Headphones : Video;
  const renew = () => { setSession(null); setLoading(true); setRefresh((n) => n + 1); };
  return <>
    <div className="mb-5 flex items-start gap-3"><span className="rounded-xl bg-[#eaf6fb] p-3 text-[#1689c1]"><Icon className="h-5 w-5" /></span><div className="min-w-0"><h3 className="break-words text-base font-semibold text-[#17384e]">{document.title}</h3><p className="mt-1 break-words text-sm text-[#607d8e]">{document.category?.name || "Uncategorized"} · {document.extension.toUpperCase()} · {formatBytes(document.size_bytes)}</p>{document.lead && <p className="mt-1 text-sm text-[#607d8e]">{document.lead.name} · #{document.lead.id}</p>}</div></div>
    {loading ? <div role="status" className="flex min-h-40 items-center justify-center gap-2 rounded-xl bg-[#f2f9fc] text-sm text-[#607d8e]"><Loader2 className="h-5 w-5 animate-spin" />Preparing secure playback…</div> : error ? <div className="space-y-3"><DocumentError message={error} /><button type="button" className={buttonClass} onClick={renew}>Retry playback</button></div> : session && <RecordingMedia key={refresh} document={document} session={session} position={position} onRenew={renew} />}
    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#e1edf2] pt-4"><p className="flex items-center gap-1.5 text-xs text-[#7892a4]"><ShieldCheck className="h-4 w-4" />Protected playback · Expiring access</p><button type="button" disabled={downloading} className={buttonClass} onClick={download}>{downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Download original</button></div>
    {downloadError && <div className="mt-3"><DocumentError message={downloadError} /></div>}
  </>;
}
